import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/firebaseConfig';
import { collection, getDocs, deleteDoc, doc, setDoc } from 'firebase/firestore';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [dbName, setDbName] = useState('jayram');
  const [collectionName, setCollectionName] = useState('');
  const [collections, setCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [deletionStats, setDeletionStats] = useState(null);

  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const fetchCollections = async () => {
    setLoadingCollections(true);
    try {
      console.group('📋 Fetching Collections from Firestore');
      console.log('Database:', dbName);

      const foundCollections = new Set();
      
      // Step 1: Get collections from registry (fast path)
      console.log('🔍 Step 1: Checking registry...');
      const registryRef = collection(db, '_collections_registry');
      try {
        const registrySnapshot = await getDocs(registryRef);
        registrySnapshot.forEach(doc => {
          foundCollections.add(doc.id);
        });
        console.log(`📋 Registry contains ${foundCollections.size} entries`);
      } catch (error) {
        console.log('⚠️ Registry not accessible');
      }

      // Step 2: Brute-force scan - Try to find all collections by pattern
      console.log('🔍 Step 2: Scanning for collections...');
      
      // Get a sample collection to extract app names
      const sampleCollections = Array.from(foundCollections);
      const potentialAppNames = new Set();
      
      // Extract app names from found collections
      sampleCollections.forEach(collName => {
        const match = collName.match(/^(.+?)_(?:components|pages|layouts|theme|content)$/);
        if (match) {
          potentialAppNames.add(match[1]);
        }
      });

      console.log(`🔍 Extracted ${potentialAppNames.size} app names from registry:`, Array.from(potentialAppNames));

      // If registry is empty or we want to be thorough, scan common patterns
      // We'll check the first few documents of known system collections to find app names
      console.log('🔍 Step 3: Checking for any collections with documents...');
      
      // Try a more aggressive approach: check all existing app names from any docs
      const testCollections = [
        '_collections_registry',
        '_ping',
        '__metadata__'
      ];

      for (const testColl of testCollections) {
        try {
          const snap = await getDocs(collection(db, testColl));
          snap.forEach(d => {
            const docId = d.id;
            // Try to extract app name patterns from document IDs
            const match = docId.match(/^(.+?)_/);
            if (match && match[1]) {
              potentialAppNames.add(match[1]);
            }
          });
        } catch (err) {
          // Collection doesn't exist
        }
      }

      console.log(`🔍 Total potential app names: ${potentialAppNames.size}`, Array.from(potentialAppNames));

      // Step 4: For each potential app name, check all pattern combinations
      console.log('🔍 Step 4: Verifying all potential collections...');
      const patterns = ['_components', '_pages', '_layouts', '_theme', '_content'];
      const verificationPromises = [];

      for (const appName of potentialAppNames) {
        for (const pattern of patterns) {
          const collName = `${appName}${pattern}`;
          verificationPromises.push(
            (async () => {
              try {
                const testRef = collection(db, collName);
                const testSnap = await getDocs(testRef);
                if (!testSnap.empty) {
                  foundCollections.add(collName);
                  console.log(`✅ Verified: ${collName} (${testSnap.size} docs)`);
                  
                  // Register it
                  try {
                    await setDoc(doc(db, '_collections_registry', collName), {
                      name: collName,
                      createdAt: new Date().toISOString(),
                      lastAccessed: new Date().toISOString()
                    });
                  } catch (err) {
                    // Ignore registration errors
                  }
                  return true;
                }
              } catch (err) {
                // Collection doesn't exist or no permission
              }
              return false;
            })()
          );
        }
      }

      // Wait for all verifications
      await Promise.all(verificationPromises);

      // Step 5: Clean up registry - remove entries that don't exist
      console.log('🔍 Step 5: Cleaning up stale registry entries...');
      const finalCollections = [];
      
      for (const collName of foundCollections) {
        try {
          const testRef = collection(db, collName);
          const testSnap = await getDocs(testRef);
          if (!testSnap.empty) {
            finalCollections.push(collName);
          } else {
            console.log(`🗑️ Removing empty: ${collName}`);
            try {
              await deleteDoc(doc(db, '_collections_registry', collName));
              foundCollections.delete(collName);
            } catch (err) {
              // Ignore cleanup errors
            }
          }
        } catch (err) {
          console.log(`🗑️ Removing non-existent: ${collName}`);
          try {
            await deleteDoc(doc(db, '_collections_registry', collName));
            foundCollections.delete(collName);
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      }

      // Sort final list
      const sortedCollections = finalCollections.sort();
      
      console.log('📊 FINAL RESULTS:');
      console.log(`   Total collections found: ${sortedCollections.length}`);
      console.log(`   Collections:`, sortedCollections);
      console.groupEnd();

      setCollections(sortedCollections);

      if (sortedCollections.length === 0) {
        showMessage('ℹ️ No collections found. Collections will appear here once created.', 'info');
      } else {
        showMessage(`✅ Found ${sortedCollections.length} collection(s)`, 'success');
      }

    } catch (error) {
      console.error('❌ Failed to fetch collections:', error);
      showMessage('⚠️ Error fetching collections. Check console for details.', 'warning');
      setCollections([]);
    } finally {
      setLoadingCollections(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  const handleDeleteCollection = async () => {
    if (!collectionName.trim()) {
      showMessage('❌ Please enter a collection name', 'error');
      return;
    }

    const confirmation = window.confirm(
      `⚠️ WARNING: You are about to delete the entire collection "${collectionName}" from database "${dbName}".\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Type the collection name again in the confirmation prompt to proceed.`
    );

    if (!confirmation) {
      showMessage('ℹ️ Deletion cancelled', 'info');
      return;
    }

    const doubleCheck = window.prompt(
      `To confirm, please type the collection name: "${collectionName}"`
    );

    if (doubleCheck !== collectionName) {
      showMessage('❌ Collection name does not match. Deletion cancelled.', 'error');
      return;
    }

    setLoading(true);
    setDeletionStats(null);

    try {
      console.group('🗑️ Deleting Collection from Firestore');
      console.log('Database:', dbName);
      console.log('Collection:', collectionName);
      
      const collectionRef = collection(db, collectionName);
      const snapshot = await getDocs(collectionRef);
      
      const totalDocs = snapshot.size;
      console.log('Total documents to delete:', totalDocs);

      if (totalDocs === 0) {
        showMessage(`ℹ️ Collection "${collectionName}" is empty or does not exist`, 'info');
        setLoading(false);
        console.groupEnd();
        return;
      }

      let deletedCount = 0;
      let failedCount = 0;
      const errors = [];

      for (const docSnapshot of snapshot.docs) {
        try {
          await deleteDoc(doc(db, collectionName, docSnapshot.id));
          deletedCount++;
          console.log(`✅ Deleted document: ${docSnapshot.id}`);
        } catch (error) {
          failedCount++;
          errors.push({ id: docSnapshot.id, error: error.message });
          console.error(`❌ Failed to delete document ${docSnapshot.id}:`, error);
        }
      }

      const stats = {
        total: totalDocs,
        deleted: deletedCount,
        failed: failedCount,
        errors
      };

      setDeletionStats(stats);

      console.log('Deletion Stats:', stats);
      console.groupEnd();

      if (failedCount === 0) {
        // Remove from registry after successful deletion
        try {
          await deleteDoc(doc(db, '_collections_registry', collectionName));
          console.log('✅ Removed from registry:', collectionName);
        } catch (err) {
          console.log('⚠️ Could not remove from registry:', err.message);
        }

        showMessage(
          `✅ Successfully deleted collection "${collectionName}" (${deletedCount} documents)`,
          'success'
        );
        setCollectionName('');
        // Refresh the collections list
        fetchCollections();
      } else {
        showMessage(
          `⚠️ Partially deleted: ${deletedCount} succeeded, ${failedCount} failed`,
          'warning'
        );
      }

    } catch (error) {
      console.error('Deletion failed:', error);
      showMessage(`❌ Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshCollections = async () => {
    await fetchCollections();
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '32px',
          color: '#FFFFFF'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ margin: '0 0 8px', fontSize: '32px', fontWeight: '700' }}>
                🔧 Admin Panel
              </h1>
              <p style={{ margin: 0, fontSize: '16px', opacity: 0.9 }}>
                Platform Administration - Firestore Collection Management
              </p>
            </div>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#667eea',
                backgroundColor: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
            >
              ← Back to App
            </button>
          </div>
        </div>

        {/* Warning Banner */}
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '2px solid #FCA5A5',
          borderRadius: '0',
          padding: '16px 32px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '24px' }}>⚠️</span>
          <div>
            <strong style={{ color: '#DC2626', fontSize: '14px', fontWeight: '600' }}>
              DANGER ZONE
            </strong>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#991B1B' }}>
              Actions performed here are irreversible. Use with extreme caution.
            </p>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div style={{
            padding: '16px 32px',
            backgroundColor: 
              messageType === 'error' ? '#FEF2F2' :
              messageType === 'success' ? '#F0FDF4' :
              messageType === 'warning' ? '#FFFBEB' :
              '#F0F9FF',
            borderBottom: `3px solid ${
              messageType === 'error' ? '#DC2626' :
              messageType === 'success' ? '#16A34A' :
              messageType === 'warning' ? '#F59E0B' :
              '#3B82F6'
            }`,
            color: 
              messageType === 'error' ? '#991B1B' :
              messageType === 'success' ? '#166534' :
              messageType === 'warning' ? '#92400E' :
              '#1E40AF',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            {message}
          </div>
        )}

        {/* Main Content */}
        <div style={{ padding: '32px' }}>
          {/* Database Selection */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Database Name
            </label>
            <input
              type="text"
              value={dbName}
              onChange={(e) => setDbName(e.target.value)}
              placeholder="e.g., jayram"
              disabled
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '14px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                backgroundColor: '#F9FAFB',
                color: '#6B7280',
                cursor: 'not-allowed'
              }}
            />
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#6B7280' }}>
              Currently connected to: <strong>{dbName}</strong>
            </p>
          </div>

          {/* Collections Dropdown */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151'
              }}>
                Select Collection <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <button
                onClick={handleRefreshCollections}
                disabled={loadingCollections}
                style={{
                  padding: '4px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#667eea',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #667eea',
                  borderRadius: '6px',
                  cursor: loadingCollections ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: loadingCollections ? 0.6 : 1
                }}
                onMouseOver={(e) => {
                  if (!loadingCollections) {
                    e.target.style.backgroundColor = '#F0F9FF';
                  }
                }}
                onMouseOut={(e) => {
                  if (!loadingCollections) {
                    e.target.style.backgroundColor = '#FFFFFF';
                  }
                }}
              >
                {loadingCollections ? '🔄 Loading...' : '🔄 Refresh'}
              </button>
            </div>
            
            <select
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              disabled={loading || loadingCollections}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '14px',
                border: '2px solid #D1D5DB',
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                cursor: loading || loadingCollections ? 'not-allowed' : 'pointer',
                transition: 'border-color 0.2s',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
            >
              <option value="">-- Select a collection --</option>
              {collections.map((coll) => (
                <option key={coll} value={coll}>
                  {coll}
                </option>
              ))}
            </select>
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#6B7280' }}>
              {collections.length > 0 
                ? `${collections.length} collection(s) available` 
                : 'No collections found. Click refresh or enter manually below.'}
            </p>
          </div>

          {/* Manual Collection Name Input */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Or Enter Collection Name Manually
            </label>
            <input
              type="text"
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              placeholder="e.g., MyApp_components, MyApp_pages"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '14px',
                border: '2px solid #D1D5DB',
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                transition: 'border-color 0.2s',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
            />
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#6B7280' }}>
              Enter the exact name if not in the dropdown above
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '32px' }}>
            <button
              onClick={handleDeleteCollection}
              disabled={loading || !collectionName.trim()}
              style={{
                flex: 1,
                padding: '14px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#FFFFFF',
                backgroundColor: loading || !collectionName.trim() ? '#9CA3AF' : '#DC2626',
                border: 'none',
                borderRadius: '8px',
                cursor: loading || !collectionName.trim() ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseOver={(e) => {
                if (!loading && collectionName.trim()) {
                  e.target.style.backgroundColor = '#B91C1C';
                  e.target.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseOut={(e) => {
                if (!loading && collectionName.trim()) {
                  e.target.style.backgroundColor = '#DC2626';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              {loading ? (
                <>
                  <span style={{ 
                    display: 'inline-block', 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid #FFFFFF',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  Deleting...
                </>
              ) : (
                <>🗑️ Delete Collection</>
              )}
            </button>

          </div>

          {/* Deletion Stats */}
          {deletionStats && (
            <div style={{
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '20px',
              marginTop: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                📊 Deletion Statistics
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>
                    Total Documents
                  </p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#374151' }}>
                    {deletionStats.total}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>
                    Successfully Deleted
                  </p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#16A34A' }}>
                    {deletionStats.deleted}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>
                    Failed
                  </p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#DC2626' }}>
                    {deletionStats.failed}
                  </p>
                </div>
              </div>

              {deletionStats.errors.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: '600', color: '#DC2626' }}>
                    Errors:
                  </h4>
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #FCA5A5',
                    borderRadius: '6px',
                    padding: '12px',
                    fontSize: '12px',
                    fontFamily: 'monospace'
                  }}>
                    {deletionStats.errors.map((err, idx) => (
                      <div key={idx} style={{ marginBottom: '8px', color: '#991B1B' }}>
                        <strong>Doc ID:</strong> {err.id}<br />
                        <strong>Error:</strong> {err.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Info Section */}
          <div style={{
            marginTop: '32px',
            padding: '20px',
            backgroundColor: '#F0F9FF',
            border: '1px solid #BFDBFE',
            borderRadius: '8px'
          }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600', color: '#1E40AF' }}>
              ℹ️ Important Notes
            </h3>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#1E40AF', lineHeight: '1.6' }}>
              <li>Collection deletion is <strong>permanent and cannot be undone</strong></li>
              <li>All documents within the collection will be deleted</li>
              <li>Subcollections (if any) will NOT be deleted automatically</li>
              <li>You will be asked to confirm twice before deletion</li>
              <li>Current database: <strong>{dbName}</strong></li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 32px',
          backgroundColor: '#F9FAFB',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
            Platform Admin Panel v1.0
          </p>
          <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
            Database: <strong>{dbName}</strong>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AdminPanel;
